import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { cn } from "@/lib/utils";
import { getAllocationStatus } from "./BudgetTab";

interface BudgetSummaryFooterProps {
  regularLines: BudgetLineRow[];
  totalLogged: number;
  totalLoggedRound: number;
  totalAssignedHoursAllPhases: number;
}

/**
 * Footer-rad nederst i timetildeling-tabellen: "Timer brukt"-strek (rundet
 * brukt / total + prosent) og "Timefordeling"-strek (fordelt vs. internal
 * total) inkludert farge-status fra getAllocationStatus.
 */
export function BudgetSummaryFooter({
  regularLines,
  totalLogged,
  totalLoggedRound,
  totalAssignedHoursAllPhases,
}: BudgetSummaryFooterProps) {
  const internalLines = regularLines.filter((l) => l.cost_type === "internal");
  const totalInternalHours = internalLines.reduce(
    (s, l) => s + (l.quantity_hours ?? 0),
    0,
  );
  const totalAssignedHours = internalLines.reduce(
    (s, l) => s + l.assignments.reduce((as, a) => as + a.hours, 0),
    0,
  );
  const overallStatus =
    totalInternalHours > 0
      ? getAllocationStatus(totalInternalHours, totalAssignedHours)
      : null;

  return (
    <div className="p-4 border-t space-y-1 text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Timer brukt:</span>
        <div className="flex items-center gap-2">
          <strong
            className={cn(
              totalLogged > totalAssignedHoursAllPhases && "text-destructive",
            )}
          >
            {totalLoggedRound}t / {totalAssignedHoursAllPhases}t
          </strong>
          {totalAssignedHoursAllPhases > 0 && (
            <span
              className={cn(
                "text-xs",
                totalLogged > totalAssignedHoursAllPhases
                  ? "text-destructive"
                  : totalLogged / totalAssignedHoursAllPhases >= 0.8
                    ? "text-amber-600"
                    : "text-muted-foreground",
              )}
            >
              ({Math.round((totalLogged / totalAssignedHoursAllPhases) * 100)}%)
            </span>
          )}
        </div>
      </div>
      {overallStatus && (
        <div className="flex justify-between border-t pt-1 mt-1 items-center">
          <span className="text-muted-foreground">Timefordeling:</span>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-[10.5px] px-2 py-0.5 rounded-full border-[1.5px] border-foreground font-medium",
                overallStatus.bg,
                overallStatus.text,
              )}
            >
              {overallStatus.label}
            </span>
            <strong>
              {totalAssignedHours} av {totalInternalHours}t fordelt
            </strong>
          </div>
        </div>
      )}
    </div>
  );
}
